// src/services/entityProjectsServices.js
import { fetchWithAuth } from "../utils/fetchWithAuth";

/* =========================
   Catálogos (fallback estático)
   ========================= */
export const PROJECT_STATUSES = ["publicado", "en_proceso", "finalizado"];

/** Fallback si /project-categories no está disponible */
export const PROJECT_CATEGORIES = [
  "energia",
  "infraestructura",
  "hidricos",
  "seguridad",
  "ciencias_cultura_artes",
  "medio_ambiental",
  "inclusion_desarrollo_social",
  "productividad_fomento_economico",
  "educacion_formacion",
  "turismo",
];

export const CATEGORY_LABELS = {
  energia: "Energía",
  infraestructura: "Infraestructura",
  hidricos: "Hídricos",
  seguridad: "Seguridad",
  ciencias_cultura_artes: "Ciencias, cultura y artes",
  medio_ambiental: "Medio ambiental",
  inclusion_desarrollo_social: "Inclusión y desarrollo social",
  productividad_fomento_economico: "Productividad y fomento económico",
  educacion_formacion: "Educación y formación",
  turismo: "Turismo",
};

/* ================ Helpers genéricos ================ */
async function jsonOrText(res) {
  // Maneja 204 No Content
  if (res.status === 204) return null;
  try {
    return await res.json();
  } catch {
    return await res.text();
  }
}
function throwIfNotOk(res, data) {
  if (!res.ok) {
    throw new Error(data?.message || (typeof data === "string" ? data : `HTTP ${res.status}`));
  }
}

/* =======================================================
   CATEGORÍAS DINÁMICAS (Backend: /project-categories)
   ======================================================= */

/** GET /project-categories  -> [{ key, label, order_index, is_active }] */
export async function listProjectCategories() {
  try {
    const res = await fetchWithAuth(`/project-categories`, { method: "GET" });
    const data = await jsonOrText(res);
    throwIfNotOk(res, data);

    const raw = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

    const arr = raw
      .filter((c) => c && (c.is_active ?? true))
      .map((c, i) => ({
        key: String(c.key),
        label: String(c.label ?? c.key),
        order_index: Number.isFinite(c.order_index) ? c.order_index : i,
        is_active: c.is_active ?? true,
      }))
      .sort(
        (a, b) =>
          (a.order_index ?? 0) - (b.order_index ?? 0) ||
          String(a.label).localeCompare(String(b.label), "es-CL")
      );

    return arr;
  } catch {
    // Fallback estático
    return PROJECT_CATEGORIES.map((key, i) => ({
      key,
      label: CATEGORY_LABELS[key] || key,
      order_index: i,
      is_active: true,
    }));
  }
}

/** Catálogo efectivo listo para el front */
export async function getEffectiveCategories() {
  const list = await listProjectCategories();
  const categories = list.map((c) => c.key);
  const labels = Object.fromEntries(list.map((c) => [c.key, c.label]));
  return { categories, labels, list };
}

/** POST /project-categories */
export async function createProjectCategory({ key, label, order_index, is_active = true }) {
  if (!key || !label) throw new Error("Faltan 'key' y/o 'label'");
  const res = await fetchWithAuth(`/project-categories`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key, label, order_index, is_active }),
  });
  const data = await jsonOrText(res);
  throwIfNotOk(res, data);
  return data?.data ?? data;
}

/** PATCH /project-categories/:key */
export async function updateProjectCategory(key, payload = {}) {
  if (!key) throw new Error("Falta 'key'");
  const res = await fetchWithAuth(`/project-categories/${encodeURIComponent(key)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await jsonOrText(res);
  throwIfNotOk(res, data);
  return data?.data ?? data;
}

/** DELETE /project-categories/:key */
export async function deleteProjectCategory(key) {
  if (!key) throw new Error("Falta 'key'");
  const res = await fetchWithAuth(`/project-categories/${encodeURIComponent(key)}`, {
    method: "DELETE",
  });
  const data = await jsonOrText(res);
  throwIfNotOk(res, data); // si backend devuelve 409 "en uso", caerá aquí y el front mostrará el mensaje
  return true;
}

/* ==================================
   CRUD de Proyectos (/projects)
   ================================== */
export async function listProjects(params = {}) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  if (params.category) qs.set("category", params.category);
  if (params.entityId) qs.set("entityId", params.entityId);
  // isPublic: solo enviar si es "true" o "false" (no enviar si es "" o undefined)
  if (params.isPublic === "true" || params.isPublic === "false") {
    qs.set("isPublic", params.isPublic);
  }
  if (params.region) qs.set("region", params.region);
  if (params.province) qs.set("province", params.province);
  if (params.district) qs.set("district", params.district);

  const qstr = qs.toString();
  const url = `/projects${qstr ? `?${qstr}` : ""}`;

  const res = await fetchWithAuth(url, { method: "GET" });
  const data = await jsonOrText(res);
  throwIfNotOk(res, data);
  return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
}

export async function getProject(id) {
  const res = await fetchWithAuth(`/projects/${id}`, { method: "GET" });
  const data = await jsonOrText(res);
  throwIfNotOk(res, data);
  return data?.data ?? data;
}

export async function createProject(payload) {
  const res = await fetchWithAuth(`/projects`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await jsonOrText(res);
  throwIfNotOk(res, data);
  return data?.data ?? data;
}

export async function updateProject(id, payload) {
  const res = await fetchWithAuth(`/projects/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await jsonOrText(res);
  throwIfNotOk(res, data);
  return data?.data ?? data;
}

export async function deleteProject(id) {
  const res = await fetchWithAuth(`/projects/${id}`, { method: "DELETE" });
  const data = await jsonOrText(res);
  throwIfNotOk(res, data);
  return true;
}
